import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Paperclip, Search, SlidersHorizontal } from "lucide-react";

import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInitData } from "@/lib/init-data";
import { formatDate } from "@/lib/format";
import {
  countActiveFilters,
  fetchMyRequests,
  formatAmount,
  isInternalTransferType,
  statusMeta,
  STATUS_FILTERS,
  type MyRequestFilters,
  type PaymentRequestRow,
} from "@/lib/payreq";
import { cn } from "@/lib/utils";
import { RequestFiltersSheet } from "@/components/request-filters-sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

const PAGE_SIZE = 20;

export default function RequestsList() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const initData = useInitData();

  const [scope, setScope] = useState<"FOR_APPROVAL" | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [filters, setFilters] = useState<MyRequestFilters>({}); // applied drawer filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState(""); // debounced, server-side
  const [rows, setRows] = useState<PaymentRequestRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Debounce the search box (300ms) — the filtering itself happens on the server.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const requestSeq = useRef(0);

  const load = useCallback(
    (pageToLoad: number, append: boolean) => {
      if (!token) return;
      const seq = ++requestSeq.current;
      if (append) setLoadingMore(true);
      else setLoading(true);
      fetchMyRequests(token, { scope, status, search, filters, currentPage: pageToLoad, pageSize: PAGE_SIZE })
        .then((d) => {
          if (seq !== requestSeq.current) return; // superseded by a newer filter/search
          setRows((prev) => (append ? [...prev, ...(d.rows ?? [])] : d.rows ?? []));
          setTotalCount(d.totalCount ?? 0);
          setPage(d.currentPage ?? pageToLoad);
        })
        .catch((e) => {
          if (seq !== requestSeq.current) return;
          toast.error(e instanceof ApiError ? e.message : "Could not load your requests.");
        })
        .finally(() => {
          if (seq !== requestSeq.current) return;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [token, scope, status, search, filters]
  );

  // Reload from page 1 whenever the filters or (debounced) search change.
  useEffect(() => {
    load(1, false);
  }, [load]);

  const hasMore = rows.length < totalCount;
  const activeFilterCount = countActiveFilters(filters);

  return (
    <div className="flex flex-col gap-3 p-4">
      {/* Mine vs assigned-to-me-for-approval (approvers only) */}
      {initData.canApproveRequests && (
        <div className="grid grid-cols-2 gap-1 rounded-lg border border-border bg-muted/40 p-1">
          {(
            [
              { value: null, label: "My requests" },
              { value: "FOR_APPROVAL", label: "For approval" },
            ] as { value: "FOR_APPROVAL" | null; label: string }[]
          ).map((t) => (
            <Button
              key={t.label}
              variant={scope === t.value ? "default" : "ghost"}
              size="sm"
              aria-pressed={scope === t.value}
              onClick={() => setScope(t.value)}
            >
              {t.label}
            </Button>
          ))}
        </div>
      )}
      {/* Search + filter drawer */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search requests…"
            className="pl-9"
            aria-label="Search requests"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          className="relative shrink-0"
          aria-label="Filters"
          onClick={() => setFiltersOpen(true)}
        >
          <SlidersHorizontal className="size-4" />
          {activeFilterCount > 0 && (
            <Badge className="absolute -top-1.5 -right-1.5 size-4 min-w-4 justify-center rounded-full p-0 text-[10px] tabular-nums">
              {activeFilterCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Status filter chips */}
      <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
        {STATUS_FILTERS.map((f) => {
          const active = status === f.value;
          return (
            <Button
              key={f.label}
              variant={active ? "default" : "outline"}
              size="sm"
              className="shrink-0 rounded-full"
              onClick={() => setStatus(f.value)}
            >
              {f.label}
            </Button>
          );
        })}
      </div>

      {/* Active drawer-filter summary */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span>
            {activeFilterCount} filter{activeFilterCount === 1 ? "" : "s"} applied
          </span>
          <span aria-hidden>·</span>
          <Button
            variant="link"
            size="xs"
            className="h-auto p-0 text-xs"
            onClick={() => setFilters({})}
          >
            Clear
          </Button>
        </div>
      )}

      {/* Rows */}
      {loading ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-16 text-center">
          <p className="text-sm font-medium">No payment requests</p>
          <p className="text-xs text-muted-foreground">
            {search || status || activeFilterCount > 0
              ? "Try a different search or filter."
              : scope === "FOR_APPROVAL"
                ? "Requests assigned to you for approval will appear here."
                : "Requests you raise will appear here."}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((r) => (
            <li key={r.id}>
              <RequestCard row={r} onOpen={() => navigate(`/requests/${r.id}`)} />
            </li>
          ))}
        </ul>
      )}

      {!loading && hasMore && (
        <Button
          variant="outline"
          className="w-full"
          disabled={loadingMore}
          onClick={() => load(page + 1, true)}
        >
          {loadingMore ? "Loading…" : `Load more (${rows.length} of ${totalCount})`}
        </Button>
      )}

      <RequestFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        token={token}
        applied={filters}
        status={status}
        onApply={(f, s) => {
          setFilters(f);
          setStatus(s);
        }}
      />
    </div>
  );
}

function RequestCard({ row, onOpen }: { row: PaymentRequestRow; onOpen: () => void }) {
  const meta = statusMeta(row.status);
  return (
    <Card
      size="sm"
      className="cursor-pointer transition-colors active:bg-muted/50"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <CardContent className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate font-mono text-xs text-muted-foreground">
              {row.receiptNumber || "—"}
            </span>
            <Badge variant="secondary" className="shrink-0 text-muted-foreground">
              {isInternalTransferType(row.reqType) ? "Internal transfer" : "Vendor payment"}
            </Badge>
            {row.pendingMyApproval && (
              <Badge className="shrink-0 border-amber-300 bg-amber-100 text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                Awaiting you
              </Badge>
            )}
          </span>
          <Badge variant="outline" className={cn("capitalize", meta.className)}>
            {meta.label}
          </Badge>
        </div>
        <div className="line-clamp-2 text-sm font-medium">
          {row.vendorName || row.reqNote || "(no description)"}
        </div>
        {(row.expenseBudgetNames || row.departmentNames) && (
          <div className="line-clamp-2 text-xs text-muted-foreground">
            {row.expenseBudgetNames}
            {row.expenseBudgetNames && row.departmentNames ? " · " : ""}
            {row.departmentNames}
          </div>
        )}
        <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
          <span className="flex items-center gap-2">
            {formatDate(row.reqDate)}
            {(row.numberOfAttachedFiles ?? 0) > 0 && (
              <span className="flex items-center gap-0.5">
                <Paperclip className="size-3" />
                {row.numberOfAttachedFiles}
              </span>
            )}
          </span>
          <span className="text-sm font-semibold text-foreground">{formatAmount(row.amountReq)}</span>
        </div>
      </CardContent>
    </Card>
  );
}

function ListSkeleton() {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card size="sm" key={i}>
          <CardContent className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-5 w-20 rounded-full" />
            </div>
            <Skeleton className="h-4 w-3/4" />
            <div className="flex items-center justify-between">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
